import { Box, Typography } from "@mui/material";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import type { MerchantListItemDto, PhoneNumber } from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import { Resources, useResource } from "Translations/Resources";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.OpenRegistrations.Table;

type Props = {
  data: Array<MerchantListItemDto>;
  isLoading?: boolean;
  rowCount?: number;
  tableProps?: Partial<MRT_TableOptions<MerchantListItemDto>>;
};

export const OpenRegistrationMerchantsTable = ({
  data,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const getResponsiveValue = useResponsiveValue();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const columns = useMemo<Array<MRT_ColumnDef<MerchantListItemDto>>>(
    () => [
      {
        accessorKey: "party.companyName",
        header: t(COMPONENT_RESOURCES.Columns.CompanyName),
        size: getResponsiveValue([150, 200]),
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: "party.companyNumber",
        header: t(COMPONENT_RESOURCES.Columns.CompanyNumber),
        size: getResponsiveValue([100, 150]),
      },
      {
        accessorKey: "party.phone",
        header: t(COMPONENT_RESOURCES.Columns.Phone),
        size: getResponsiveValue([120, 140]),
        enableSorting: false,
        Cell: ({ cell }) => formatPhone(cell.getValue<PhoneNumber>()),
      },
      {
        accessorKey: "party.email",
        header: t(COMPONENT_RESOURCES.Columns.Email),
        size: getResponsiveValue([150, 250]),
        enableSorting: false,
      },
    ],
    [getResponsiveValue, t],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      ...tableProps?.state,
      isLoading,
    },
    initialState: {
      ...tableProps?.initialState,
      density: getResponsiveValue(["compact", "comfortable"]),
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("merchant-complete-registration")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
    renderTopToolbarCustomActions: () => (
      <Box height="44px" display="flex" alignItems="center" pl={[0, 2]} gap={3}>
        <Typography variant="h2" lineHeight={1.4} fontSize={[16, 18, 22]}>
          {t(COMPONENT_RESOURCES.Title)}
        </Typography>
      </Box>
    ),
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  return <MaterialReactTable table={table} />;
};
